import React, { createContext, useContext, useEffect, useState } from 'react';

import { PaymentError } from '../core/payment-error';
import I18nStore, { I18nConfig, I18nMessages } from '../utils/i18n';

interface I18nContextType {
    t: (key: string, fallback?: string, params?: string[]) => string;
    isHidden: (key: string) => boolean;
    locale: string;
    isLoading: boolean;
    setOverrides: (overrides: Partial<I18nMessages>) => void;
}

const I18nContext = createContext<I18nContextType | undefined>(undefined);

interface I18nProviderProps {
    children: React.ReactNode;
    config: I18nConfig;
}

export const I18nProvider: React.FC<I18nProviderProps> = ({
    children,
    config
}) => {
    const [i18n] = useState(() => new I18nStore(config));
    const [isLoading, setIsLoading] = useState(true);
    const [, setVersion] = useState(0); // Force re-renders when overrides change

    useEffect(() => {
        const controller = new AbortController();
        let isMounted = true;

        i18n.setLocale(config.locale);

        const loadTranslations = async () => {
            setIsLoading(true);
            await i18n.loadMessages(controller.signal);
            if (!isMounted) {
                return;
            }
            setIsLoading(false);
        };

        void loadTranslations();

        return () => {
            isMounted = false;
            controller.abort();
        };
    }, [i18n, config.locale]);

    const setOverrides = (overrides: Partial<I18nMessages>) => {
        i18n.setOverrides(overrides);
        setVersion((v) => v + 1); // Force re-render
    };

    const contextValue: I18nContextType = {
        t: i18n.t.bind(i18n),
        isHidden: i18n.isHidden.bind(i18n),
        locale: i18n.getLocale(),
        isLoading,
        setOverrides
    };

    return (
        <I18nContext.Provider value={contextValue}>
            {children}
        </I18nContext.Provider>
    );
};

export const useI18n = (): I18nContextType => {
    const context = useContext(I18nContext);
    if (!context) {
        throw new PaymentError(
            'useI18n must be used within an I18nProvider',
            'VALIDATION_ERROR'
        );
    }
    return context;
};
